import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  Check,
  X,
  ExternalLink,
  ShieldCheck,
  PhoneCall,
  MessageSquare,
  MessageCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Lock,
  Ban,
} from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { resolveNoDepositOverride } from '@/domain/approvals';
import { ensureConfirmationCall, resolveConfirmationOutcome } from '@/domain/confirmationFlow';
import { askArtistToConfirm } from '@/domain/confirmationEscalationFlow';
import { markNoShowBooking, opsCancelBooking } from '@/domain/opsCancelFlow';
import { opsExtendPayBy } from '@/domain/opsExtendFlow';
import { messageAboutOpsItem } from '@/domain/opsThreadLoop';
import { resolveNoRebookFlag } from '@/domain/resolveNoRebookFlow';
import { resolveCameDownIssue } from '@/domain/cameDownFlow';
import { canMarkConfirmed } from '@/domain/permissions';
import { selfPersonIdFromSession } from '@/utils/commsHelpers';
import { actorLabel, currentActorId } from '@/domain/actor';
import { formatStudioDay } from '@/utils/studioDay';
import { clearTopupMoveRequest } from '@/domain/topupMoveRequest';
import { aud, customerName, simDateShort, simTimeFull, studioDisplay } from '@/utils/format';
import { applyCancelSplit, isLiveBooking, isPastBooking, ledgerPaidAud, studioDayKey, studioDayStartIso } from '@atink/domain';
import OpsBookingSummary from '@/components/ops/OpsBookingSummary';
import UnclosedLinkBody from '@/components/ops/UnclosedLinkBody';
import PayByExtensionBody from '@/components/ops/PayByExtensionBody';
import type { OpsItem } from '@/domain/opsWorklist';
import type { Booking, CustomerRecord, Deposit, NoRebookFlag, StudioId } from '@/domain/types';

interface Props {
  item: OpsItem;
  onClose: () => void;
}

// The one Ops action modal (vault Diagram 09). Opened from a worklist row; each
// kind renders its own decision node - the layout language mirrors the
// ConfirmationWorkModal (overlay + panel + header + typed-reason gates). Ops-only
// actions (override approve/decline, cancel) are reached only from /ops, which is
// georgina-only. Light theme tokens throughout.

export default function OpsWorkModal({ item, onClose }: Props) {
  const navigate = useNavigate();
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const sessionArtistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const artistsById = useBrainStore((s) => s.artists);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const [reason, setReason] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [sentThreadId, setSentThreadId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // `by` is the signed-in person's id on the domain stamps (14ym9bv75ky: the
  // person, not the persona slug); `opsPersonId` is the comms person who authors
  // DMs (a signed-out session is treated as Ops).
  const by = currentActorId() ?? 'georgina';
  const opsPersonId =
    selfPersonIdFromSession({ persona, agentId, artistId: sessionArtistId, apprenticeId }) ?? by;

  const nameFor = (customerId: string): string => {
    const c: CustomerRecord | undefined = customers[customerId];
    return c ? customerName(c) : customerId.slice(-6);
  };

  const notify = (title: string, body?: string, intensity: 'high' | 'info' = 'info') =>
    pushNotification({ intensity, title, body, ttlMs: 4000 });

  const act = <T,>(fn: () => T): void => {
    const result = fn();
    if (result instanceof Promise) void result.then((ok) => { if (ok !== false) onClose(); });
    else onClose();
  };

  const { title, subtitle, customerId } = headerFor(item, nameFor);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-cream/10 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ops-work-modal"
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        className="relative panel bg-ink-deep max-w-[1000px] w-full max-h-[88vh] rounded-xl flex flex-col overflow-hidden shadow-gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-4 border-b border-gold-dim/20 px-6 py-4">
          <div className="min-w-0">
            <p className="label-small-caps text-gold-bright">{sectionLabel(item)}</p>
            <h2 className="heading-display text-2xl text-cream truncate mt-1">{title}</h2>
            <p className="font-serif text-sm text-cream-soft mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {customerId ? (
              <button
                type="button"
                onClick={() => navigate(`/customer/${customerId}`)}
                className="btn-quiet text-xs"
                data-testid="owm-open-record"
              >
                <ExternalLink size={12} /> Open record
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="btn-quiet"
              aria-label="Close"
              data-testid="owm-close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 min-h-0 overflow-y-auto">
          {/* LEFT: the booking + customer context behind the decision (Diagram 09) */}
          <aside className="md:border-r border-gold-dim/20 px-6 py-5">
            <p className="label-small-caps text-cream-faint mb-3">{leftHeader(item)}</p>
            <LeftContext item={item} bookings={bookings} />
          </aside>

          {/* RIGHT: the decision itself */}
          <div className="px-6 py-5 flex flex-col gap-4 min-w-0">
            <p className="label-small-caps text-cream-faint">{rightHeader(item)}</p>
            {item.kind === 'override' ? (
              <OverrideBody
                note={item.request.note}
                reason={reason}
                setReason={setReason}
                onApprove={() =>
                  act(() => {
                    resolveNoDepositOverride({
                      requestId: item.request.id,
                      approve: true,
                      resolvedBy: by,
                    });
                    notify(
                      'No-deposit booking approved',
                      'Confirmed, paying in full on the day.',
                      'high',
                    );
                  })
                }
                onDecline={() => {
                  const trimmed = reason.trim();
                  if (!trimmed) return;
                  act(() => {
                    // Sets the customer's decline flag and puts the $0 hold on a
                    // pay-by link, in the same server step (B3).
                    resolveNoDepositOverride({
                      requestId: item.request.id,
                      approve: false,
                      resolvedBy: by,
                      resolutionNote: trimmed,
                    });
                    notify(
                      'No-deposit request declined',
                      'Flagged - the Agent must now collect a deposit before the pay-by.',
                    );
                  });
                }}
              />
            ) : null}

            {item.kind === 'confirmation' ? (
              <ConfirmationBody
                booking={item.booking}
                clockIso={clockIso}
                artistAsked={item.artistAsked}
                deadlinePassed={item.deadlinePassed}
                artistAskDeadlineIso={item.artistAskDeadlineIso}
                canConfirm={persona === null || canMarkConfirmed(persona)}
                reason={reason}
                setReason={setReason}
                onAskArtist={() =>
                  act(() => {
                    if (!opsPersonId) return;
                    askArtistToConfirm(item.booking.id, { byPersonId: opsPersonId, at: clockIso });
                    notify(
                      'Asked the artist',
                      'Asked them to reach out from their IG with a deadline.',
                    );
                  })
                }
                onMarkConfirmed={() =>
                  act(() => {
                    const callId = ensureConfirmationCall(item.booking.id, { at: clockIso });
                    if (!callId) return;
                    resolveConfirmationOutcome(callId, 'confirmed', { by, at: clockIso });
                    notify('Marked confirmed', 'The booking is locked in.', 'high');
                  })
                }
                onCancel={() => {
                  if (!reason.trim()) return;
                  act(async () => {
                    const callId = ensureConfirmationCall(item.booking.id, { at: clockIso });
                    if (!callId) return;
                    const res = resolveConfirmationOutcome(callId, 'cancelled', { by, at: clockIso, reason });
                    if (!res.ok || (res.completion && !(await res.completion))) return false;
                    notify('Cancelled', 'Slot freed to the Sweeper.');
                  });
                }}
                onNoShow={() => {
                  const trimmed = reason.trim();
                  if (!trimmed) return;
                  act(async () => {
                    const res = markNoShowBooking({ bookingId: item.booking.id, reason: trimmed, by, source: 'ops', at: clockIso });
                    if (!res.ok || (res.completion && !(await res.completion))) return false;
                    if (res.ok) notify('Marked no-show', res.amountAud ? `${aud(res.amountAud)} deposit forfeited.` : 'No deposit on file.');
                  });
                }}
              />
            ) : null}

            {item.kind === 'topup-extension' && item.booking ? (
              <ExtendBody
                payByIso={item.payByIso}
                clockIso={clockIso}
                reason={reason}
                setReason={setReason}
                canCancel={!!item.plan.bookingId}
                booking={item.booking}
                onExtend={(newDueIso) =>
                  act(async () => {
                    const res = opsExtendPayBy({ planId: item.plan.id, newDueIso, by });
                    if (!res.ok || (res.completion && !(await res.completion))) return false;
                    notify('Pay-by extended', 'Reminder re-pointed to the new date. The chase starts again.');
                  })
                }
                onCancel={() => {
                  const bookingId = item.plan.bookingId;
                  const trimmed = reason.trim();
                  if (!bookingId || !trimmed) return;
                  act(async () => {
                    const res = opsCancelBooking({ bookingId, reason: trimmed, by });
                    if (!res.ok || (res.completion && !(await res.completion))) return false;
                    notify('Cancelled', 'Slot freed to the Sweeper.');
                  });
                }}
              />
            ) : null}

            {item.kind === 'unpaid-artist' ? (
              <UnpaidArtistBody
                studioId={item.studioId}
                artistName={item.artistName}
                amountAud={item.amountAud}
                dayIso={item.dayIso}
                draft={messageDraft}
                setDraft={setMessageDraft}
                sent={messageSent}
                onSend={(managerId, body) => {
                  // W5 mj: the DM carries a persisted link back to this row, so the
                  // row shows awaiting / replied across reloads (opsThreadState).
                  const { threadId } = messageAboutOpsItem({
                    itemId: item.id,
                    fromPersonId: opsPersonId,
                    toPersonId: managerId,
                    body,
                    at: clockIso,
                  });
                  setMessageSent(true);
                  setSentThreadId(threadId);
                  notify('Message sent', 'The studio manager will reply in the thread.');
                }}
                onOpenThread={() => {
                  if (sentThreadId) {
                    navigate(`/comms?dm=${sentThreadId}`);
                    onClose();
                  }
                }}
              />
            ) : null}

            {item.kind === 'unclosed-link' && !item.readOnly ? (
              <UnclosedLinkBody
                link={item.link}
                customerName={nameFor(item.link.customerId)}
                artistName={item.link.artistId ? artistsById[item.link.artistId]?.displayName : undefined}
                by={by}
                fromPersonId={opsPersonId}
                nowIso={clockIso}
                onDone={onClose}
              />
            ) : null}

            {item.kind === 'payby-extension' ? (
              <PayByExtensionBody item={item} by={by} onDone={onClose} />
            ) : null}

            {item.kind === 'came-down' ? (
              <CameDownBody
                booking={item.booking}
                artistName={item.artistName}
                onResolve={() =>
                  act(() => {
                    resolveCameDownIssue(item.booking.id, { by: actorLabel(by), fallback: item.booking });
                    notify('Walk-down issue resolved', `${item.artistName}'s checkout is off the list.`, 'high');
                  })
                }
              />
            ) : null}

            {item.kind === 'no-rebook' ? (
              <NoRebookBody
                flag={item.flag}
                onOpenThread={() => {
                  navigate(`/customer/${item.flag.customerId}`);
                  onClose();
                }}
                onMarkComplete={() =>
                  act(() => {
                    resolveNoRebookFlag(item.flag.id, { by });
                    notify(
                      'No-rebook flag cleared',
                      'Marked complete - the Sweeper keeps re-calling.',
                      'high',
                    );
                  })
                }
              />
            ) : null}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// --- header helpers ---------------------------------------------------------

function sectionLabel(item: OpsItem): string {
  switch (item.kind) {
    case 'override':
      return 'No-deposit override';
    case 'confirmation':
      return 'Confirmation escalated';
    case 'topup-extension':
      return 'Inside-7d top-up extension';
    case 'unpaid-artist':
      return 'Unpaid artist';
    case 'no-rebook':
      return 'No rebook · mid piece';
    case 'came-down':
      return 'Not walked down';
    case 'unclosed-link':
      return 'Links >48h';
    case 'payby-extension':
      return 'Pay-by extensions';
    default:
      return 'Ops';
  }
}

function headerFor(
  item: OpsItem,
  nameFor: (id: string) => string,
): { title: string; subtitle: string; customerId?: string } {
  switch (item.kind) {
    case 'override':
      return {
        title: item.booking ? nameFor(item.booking.customerId) : 'No-deposit request',
        subtitle: `Requested by ${actorLabel(item.request.requestedBy)}${
          item.booking ? ` · ${studioDisplay(item.booking.studioId)}` : ''
        }`,
        customerId: item.booking?.customerId,
      };
    case 'confirmation':
      return {
        title: nameFor(item.booking.customerId),
        subtitle: `${studioDisplay(item.booking.studioId)} · ${simTimeFull(item.booking.start)}`,
        customerId: item.booking.customerId,
      };
    case 'topup-extension':
      return {
        title: nameFor(item.plan.customerId),
        subtitle: `${studioDisplay(item.plan.studioId)} · inside 7 days`,
        customerId: item.plan.customerId,
      };
    case 'unpaid-artist':
      return {
        title: item.artistName,
        subtitle: `${studioDisplay(item.studioId)} · ${aud(item.amountAud)} owed for ${simDateShort(item.dayIso)}`,
      };
    case 'no-rebook':
      return {
        title: nameFor(item.flag.customerId),
        subtitle: `${studioDisplay(item.flag.studioId)} · mid-piece, no rebook booked`,
        customerId: item.flag.customerId,
      };
    case 'came-down':
      return {
        title: nameFor(item.booking.customerId),
        subtitle: `${studioDisplay(item.booking.studioId)} · ${item.artistName} didn’t walk the client down`,
        customerId: item.booking.customerId,
      };
    case 'unclosed-link':
      return {
        title: nameFor(item.link.customerId),
        subtitle: `${studioDisplay(item.link.studioId)} · unpaid deposit link`,
        customerId: item.link.customerId,
      };
    case 'payby-extension':
      return {
        title: nameFor(item.request.customerId),
        subtitle: `${studioDisplay(item.request.studioId)} · deposit link · asked ${item.ageLabel}${item.ageLabel === 'just now' ? '' : ' ago'} · inside 7 days`,
        customerId: item.request.customerId,
      };
    default:
      return { title: 'Ops item', subtitle: '' };
  }
}

// --- layout: the context column beside every decision -----------------------

function leftHeader(item: OpsItem): string {
  switch (item.kind) {
    case 'unpaid-artist':
      return 'The payout';
    case 'no-rebook':
      return 'The piece & customer';
    default:
      return 'The booking & customer';
  }
}

function rightHeader(item: OpsItem): string {
  switch (item.kind) {
    case 'unpaid-artist':
      return 'The message';
    case 'no-rebook':
      return 'Reason & action';
    case 'unclosed-link':
      return 'The link & close-off';
    default:
      return 'Your call';
  }
}

function LeftContext({ item, bookings }: { item: OpsItem; bookings: Record<string, Booking> }) {
  switch (item.kind) {
    case 'override':
      return <OpsBookingSummary customerId={item.booking?.customerId} booking={item.booking} />;
    case 'confirmation':
      return <OpsBookingSummary customerId={item.booking.customerId} booking={item.booking} />;
    case 'topup-extension': {
      // The money at stake: instalments still owed on the plan (tv-0q4).
      const outstanding = item.plan.instalments
        .filter((i) => i.status !== 'paid')
        .reduce((sum, i) => sum + i.amountAud, 0);
      return (
        <>
          <OpsBookingSummary
            customerId={item.plan.customerId}
            booking={item.booking}
            money={{ label: 'Outstanding', value: aud(outstanding), stake: true }}
          />
          {item.requestedStartIso ? (
            // D01-17: the Agent sent this over with the date the client asked for.
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <p className="font-serif text-sm text-gold-bright" data-testid="owm-move-request">
                The client asked to move to {formatStudioDay(item.requestedStartIso, 'EEE d MMM')}
                {item.plan.moveRequestedBy ? ` · sent by ${actorLabel(item.plan.moveRequestedBy)}` : ''}
              </p>
              <MoveRequestDone planId={item.plan.id} />
            </div>
          ) : null}
        </>
      );
    }
    case 'unpaid-artist':
      return (
        <PayoutSummary
          studioId={item.studioId}
          artistName={item.artistName}
          amountAud={item.amountAud}
          dayIso={item.dayIso}
        />
      );
    case 'no-rebook':
      return (
        <OpsBookingSummary
          customerId={item.flag.customerId}
          booking={bookings[item.flag.sourceBookingId]}
          pieceId={item.flag.pieceId}
          whenLabel="Last sat"
        />
      );
    case 'unclosed-link':
      return (
        <OpsBookingSummary
          customerId={item.link.customerId}
          booking={item.link.heldBookingId ? bookings[item.link.heldBookingId] : undefined}
          money={{ label: 'Unpaid link', value: aud(item.link.amountAud), stake: true }}
        />
      );
    case 'came-down':
      return <OpsBookingSummary customerId={item.booking.customerId} booking={item.booking} whenLabel="Session" />;
    case 'payby-extension':
      return (
        <OpsBookingSummary
          customerId={item.request.customerId}
          booking={item.booking}
          {...(item.deposit ? { money: { label: 'Unpaid link', value: aud(item.deposit.amountAud), stake: true } } : {})}
        />
      );
    default:
      return null;
  }
}

function PayoutSummary({
  studioId,
  artistName,
  amountAud,
  dayIso,
}: {
  studioId: StudioId;
  artistName: string;
  amountAud: number;
  dayIso: string;
}) {
  return (
    <div className="panel-dark p-3.5 grid grid-cols-2 gap-x-4 gap-y-3" data-testid="owm-summary">
      <SummaryField k="Artist" v={artistName} />
      <SummaryField k="Studio" v={studioDisplay(studioId)} />
      <SummaryField k="Owed" v={aud(amountAud)} stake />
      <SummaryField k="For" v={simDateShort(dayIso)} />
    </div>
  );
}

function SummaryField({ k, v, stake }: { k: string; v: string; stake?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="label-small-caps">{k}</p>
      <p
        className={clsx(
          'font-serif text-sm mt-0.5',
          stake ? 'text-blood font-semibold' : 'text-cream',
        )}
      >
        {v}
      </p>
    </div>
  );
}

/**
 * L5 (tv-hrsz, D10-14): a checkout where the artist did NOT walk the client
 * down. Ops follows it up with the studio, then resolves it (stamped on the
 * booking, so it stays off the list after a reload).
 */
function CameDownBody({
  booking,
  artistName,
  onResolve,
}: {
  booking: Booking;
  artistName: string;
  onResolve: () => void;
}) {
  const recordedBy = booking.checkedOutBy
    ? `Recorded at checkout by ${actorLabel(booking.checkedOutBy)}`
    : 'Recorded at checkout · staff member not recorded';
  return (
    <>
      <div className="panel-dark p-3" data-testid="owm-came-down">
        <p className="label-small-caps">What happened</p>
        <p className="font-serif text-sm text-cream mt-1.5">
          {artistName} didn&rsquo;t walk the client down to the desk at checkout.
        </p>
        <p className="font-mono text-[11px] text-cream-faint mt-2">
          {recordedBy}
          {booking.checkedOutAt ? ` · ${simDateShort(booking.checkedOutAt)}` : ''}
        </p>
      </div>
      <p className="font-serif text-[12px] text-cream-soft">
        Follow it up with the studio manager, then mark it resolved.
      </p>
      <button
        type="button"
        onClick={onResolve}
        className="btn-primary w-full justify-center text-xs"
        data-testid="owm-came-down-resolve"
      >
        Mark resolved
      </button>
    </>
  );
}

function NoRebookBody({
  flag,
  onOpenThread,
  onMarkComplete,
}: {
  flag: NoRebookFlag;
  onOpenThread: () => void;
  onMarkComplete: () => void;
}) {
  const bookings = useBrainStore((s) => s.bookings);
  // Real checkout attribution (tv-ona): whoever ran checkout on the source
  // booking. When the booking predates the checkedOutBy capture it reads "not
  // recorded" rather than guessing the studio manager.
  const checkedOutById = bookings[flag.sourceBookingId]?.checkedOutBy;
  const recordedBy = checkedOutById
    ? `Recorded at checkout by ${actorLabel(checkedOutById)}`
    : 'Recorded at checkout · staff member not recorded';

  return (
    <>
      <div className="panel-dark p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="label-small-caps">Why no rebook</p>
          <span className="rounded-full border border-gold-dim/40 px-2 py-0.5 text-[10px] font-inter uppercase tracking-wider text-cream-faint">
            from checkout
          </span>
        </div>
        <p
          className="font-serif text-sm text-cream italic mt-1.5"
          data-testid="owm-norebook-reason"
        >
          &ldquo;{flag.reason}&rdquo;
        </p>
        <p className="font-mono text-[11px] text-cream-faint mt-2">
          {recordedBy} · {simDateShort(flag.createdAt)}
        </p>
      </div>

      <div className="rounded-md border border-gold-dim/25 bg-ink-black/30 p-3">
        <p className="label-small-caps text-gold-bright">Where it routes</p>
        <ul className="mt-2 space-y-1.5 font-serif text-sm text-cream">
          <li className="flex items-start gap-2">
            <span className="h-2 w-2 rounded-full bg-gold-bright shrink-0 mt-1.5" />
            <span>
              <span className="font-semibold">Ops flag</span> - this item, for accountability
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="h-2 w-2 rounded-full bg-azure shrink-0 mt-1.5" />
            <span>
              <span className="font-semibold">Sweeper</span> - re-calling for gap-fills ·
              recent-tattoo flag
            </span>
          </li>
        </ul>
      </div>

      <p className="font-serif text-xs text-cream-soft">
        No rebook link needed - the Sweeper already re-calls. Mark complete once the follow-up is
        handled.
      </p>

      <div className="flex items-center justify-end gap-2 mt-auto">
        <button
          type="button"
          onClick={onOpenThread}
          className="inline-flex items-center gap-1.5 rounded-md border border-gold-dim/40 px-3 py-1.5 text-xs text-cream-soft hover:border-gold-bright/50 transition"
          data-testid="owm-open-thread"
        >
          <MessageCircle size={13} /> Open thread
        </button>
        <button
          type="button"
          onClick={onMarkComplete}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition"
          data-testid="owm-mark-complete"
        >
          <CheckCircle2 size={13} /> Mark complete
        </button>
      </div>
    </>
  );
}

// --- per-kind bodies --------------------------------------------------------

function OverrideBody({
  note,
  reason,
  setReason,
  onApprove,
  onDecline,
}: {
  note: string;
  reason: string;
  setReason: (v: string) => void;
  onApprove: () => void;
  onDecline: () => void;
}) {
  return (
    <>
      <div className="panel-dark p-3">
        <p className="label-small-caps">Their reason</p>
        <p className="font-serif text-sm text-cream mt-1">&ldquo;{note}&rdquo;</p>
      </div>
      <p className="font-serif text-xs text-cream-soft">
        Approve to confirm the booking with the deposit waived, or decline (with a reason) so the
        Agent must chase a deposit - no second trip through Ops.
      </p>
      <label className="block">
        <span className="label-small-caps">Decline reason</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. new customer - take a $200 deposit link instead."
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
          data-testid="owm-decline-reason"
        />
      </label>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDecline}
          disabled={!reason.trim()}
          className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="owm-decline"
        >
          <X size={13} /> Decline
        </button>
        <button
          type="button"
          onClick={onApprove}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition"
          data-testid="owm-approve"
        >
          <ShieldCheck size={13} /> Approve
        </button>
      </div>
    </>
  );
}

function ConfirmationBody({
  booking,
  clockIso,
  artistAsked,
  deadlinePassed,
  artistAskDeadlineIso,
  canConfirm,
  reason,
  setReason,
  onAskArtist,
  onMarkConfirmed,
  onCancel,
  onNoShow,
}: {
  booking: Booking;
  clockIso: string;
  artistAsked: boolean;
  /** 8xat: the artist ask's deadline passed with no confirmation. */
  deadlinePassed?: boolean;
  artistAskDeadlineIso?: string;
  canConfirm: boolean;
  reason: string;
  setReason: (v: string) => void;
  onAskArtist: () => void;
  onMarkConfirmed: () => void;
  onCancel: () => void;
  /** W5b Q5/Q6: a past, never-attended booking is marked no-show, never cancelled. */
  onNoShow: () => void;
}) {
  const past = isPastBooking(booking.start, clockIso);
  return (
    <>
      <p className="font-serif text-xs text-cream-soft">
        Three sequences unanswered. Ask the artist to reach out from their IG with a deadline, mark
        it confirmed once they report back, or cancel.
      </p>
      {deadlinePassed ? (
        <p
          className="rounded-md border border-blood/40 bg-blood/5 px-3 py-2 text-[12px] text-blood"
          data-testid="owm-deadline-passed"
        >
          Deadline passed
          {artistAskDeadlineIso ? ` (${format(new Date(artistAskDeadlineIso), 'EEE d MMM')})` : ''} with no word
          from the artist. Cancel unless they have just reported back.
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onAskArtist}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-azure/50 bg-azure/10 px-3 py-2 text-sm text-azure hover:bg-azure/20 transition"
          data-testid="owm-ask-artist"
        >
          <PhoneCall size={14} />{' '}
          {artistAsked ? 'Ask the artist again' : 'Ask the artist to reach out'}
        </button>
        {canConfirm ? (
          <button
            type="button"
            onClick={onMarkConfirmed}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-2 text-sm text-mint hover:bg-mint/20 transition"
            data-testid="owm-mark-confirmed"
          >
            <Check size={14} /> Mark confirmed
          </button>
        ) : null}
      </div>
      <label className="block border-t border-gold-dim/20 pt-3">
        <span className="label-small-caps">Cancel reason</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. no response by the deadline."
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
          data-testid="owm-cancel-reason"
        />
      </label>
      {past ? (
        <div className="rounded-md border border-blood/40 bg-blood/5 px-3 py-2.5" data-testid="owm-no-show-consequence">
          <p className="label-small-caps text-blood">This appointment has passed</p>
          <p className="font-serif text-xs text-cream-soft mt-1">
            It can't be cancelled. Mark it a no-show: any deposit paid is forfeited to the house.
          </p>
        </div>
      ) : (
        <CancelConsequence booking={booking} clockIso={clockIso} />
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={past ? onNoShow : onCancel}
          disabled={!reason.trim()}
          className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={past ? 'owm-no-show' : 'owm-cancel'}
        >
          <Ban size={13} /> {past ? 'Mark no-show' : 'Cancel booking'}
        </button>
      </div>
    </>
  );
}

// --- extend: the two-state "move top-up date" flow --------------------------

const DAY_LABEL_FMT = 'EEE d MMM';
const studioCalendarDate = (iso: string): Date => new Date(`${studioDayKey(iso)}T00:00:00`);
const dayLabel = (d: Date): string => format(d, DAY_LABEL_FMT);

// `unselectable` covers any day the plan can't move to (past the clock OR on/before
// the current pay-by), distinct from `muted` adjacent-month cells.
type DayState = 'appt' | 'current' | 'booked' | 'selected' | 'muted' | 'unselectable';

function ExtendBody({
  payByIso,
  clockIso,
  reason,
  setReason,
  canCancel,
  booking,
  onExtend,
  onCancel,
}: {
  payByIso?: string;
  clockIso: string;
  reason: string;
  setReason: (v: string) => void;
  canCancel: boolean;
  booking: Booking;
  onExtend: (newDueIso: string) => void;
  onCancel: () => void;
}) {
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);

  // The calendar is locked to this booking's artist by default; switching only
  // changes whose bookings show for context - it never moves the plan.
  const [selectedArtistId, setSelectedArtistId] = useState<string>(booking.artistId);
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(studioCalendarDate(payByIso ?? booking.start)),
  );
  // A day highlighted before it is committed, then the placed date (ISO) that
  // flips the body into its confirm state.
  const [picked, setPicked] = useState<{ key: string; date: Date } | null>(null);
  const [placed, setPlaced] = useState<{ iso: string; label: string } | null>(null);

  const studioArtists = useMemo(() => {
    const list = Object.values(artists)
      .filter((a) => a.studioId === booking.studioId && !a.archived)
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
    // Always keep the booking's own artist selectable, even if archived, so the
    // controlled <select> value never falls out of its option set.
    const own = artists[booking.artistId];
    if (own && !list.some((a) => a.id === own.id)) list.unshift(own);
    return list;
  }, [artists, booking.studioId, booking.artistId]);

  const selectedArtist = artists[selectedArtistId];
  const artistLabel = selectedArtist
    ? `${selectedArtist.firstName} ${selectedArtist.lastName}`.trim()
    : 'this artist';
  const artistInitials = selectedArtist
    ? `${selectedArtist.firstName[0] ?? ''}${selectedArtist.lastName[0] ?? ''}`.toUpperCase()
    : '··';

  // The selected artist's LIVE bookings - both the calendar dots and the agenda
  // list read from this. A moved booking's kept original (superseded), a
  // cancellation or a no-show is never booked context (tv-2na.11.14).
  const artistBookings = useMemo(
    () =>
      Object.values(bookings)
        .filter((b) => b.artistId === selectedArtistId && isLiveBooking(b))
        .sort((a, b) => a.start.localeCompare(b.start)),
    [bookings, selectedArtistId],
  );
  const bookedKeys = useMemo(
    () => new Set(artistBookings.map((b) => studioDayKey(b.start))),
    [artistBookings],
  );

  const apptKey = studioDayKey(booking.start);
  const payByKey = payByIso ? studioDayKey(payByIso) : undefined;
  const payByTime = studioCalendarDate(payByIso ?? clockIso).getTime();
  const clockTime = studioCalendarDate(clockIso).getTime();

  // Grid: whole weeks (Mon-first) covering the visible month.
  const gridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const customer = customers[booking.customerId];
  const firstName = customer?.firstName ?? 'there';
  const phone = customer?.phone;

  // opsExtendPayBy refuses a date <= the current due, so the UI never offers one:
  // a day is selectable only when it is strictly past the pay-by AND not before now.
  const isSelectable = (day: Date): boolean =>
    isSameMonth(day, viewMonth) && day.getTime() > payByTime && day.getTime() >= clockTime;

  const dayState = (day: Date): DayState | undefined => {
    if (!isSameMonth(day, viewMonth)) return 'muted';
    const key = format(day, 'yyyy-MM-dd');
    if (picked?.key === key) return 'selected';
    if (key === apptKey) return 'appt';
    if (payByKey && key === payByKey) return 'current';
    if (bookedKeys.has(key)) return 'booked';
    if (!isSelectable(day)) return 'unselectable';
    return undefined;
  };

  if (placed) {
    const oldLabel = payByIso ? dayLabel(studioCalendarDate(payByIso)) : '—';
    const smsBody = `Hi ${firstName}, no worries — we've moved your top-up date to ${placed.label}. Same booking, same spot. Reply here if anything changes. — Addikted to Ink`;
    return (
      <ExtendConfirmState
        oldLabel={oldLabel}
        newLabel={placed.label}
        smsBody={smsBody}
        phone={phone}
        onChangeDate={() => setPlaced(null)}
        onConfirm={() => onExtend(placed.iso)}
      />
    );
  }

  return (
    <>
      <p className="font-serif text-xs text-cream-soft">
        Pick the new top-up date on <span className="text-cream">{artistLabel}</span>&rsquo;s
        calendar - their other bookings show for context, so the date lands sensibly against the
        sitting. Switch artists if the piece is moving to someone else.
      </p>

      <div className="flex items-center justify-between gap-3 rounded-md border border-gold-dim/25 bg-ink-black/30 px-3 py-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-gold-bright/15 text-gold-bright text-xs font-semibold shrink-0">
            {artistInitials}
          </span>
          <div className="min-w-0">
            <p className="font-serif text-sm text-cream truncate">{artistLabel}</p>
            <p className="flex items-center gap-1 font-mono text-[10px] text-cream-faint">
              <Lock size={9} /> Locked to this booking&rsquo;s artist
            </p>
          </div>
        </div>
        <label className="shrink-0">
          <span className="sr-only">Switch artist</span>
          <select
            value={selectedArtistId}
            onChange={(e) => {
              setSelectedArtistId(e.target.value);
              setPicked(null);
            }}
            className="bg-ink-black/40 border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream-soft focus:outline-none focus:border-gold-bright"
            data-testid="owm-extend-artist"
          >
            {studioArtists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.firstName} {a.lastName}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ExtendCalendar
        viewMonth={viewMonth}
        days={gridDays}
        dayState={dayState}
        isSelectable={isSelectable}
        legendArtistName={selectedArtist?.firstName ?? 'Artist'}
        onPick={(day) => setPicked({ key: format(day, 'yyyy-MM-dd'), date: day })}
        onNavMonth={(delta) => setViewMonth((m) => addMonths(m, delta))}
      />

      {artistBookings.length ? (
        <div
          className="rounded-md border border-gold-dim/25 bg-ink-black/30 p-2.5"
          data-testid="owm-extend-agenda"
        >
          <p className="label-small-caps text-cream-faint">
            {artistLabel} · bookings around this window
          </p>
          <ul className="mt-1.5 space-y-1">
            {artistBookings.slice(0, 5).map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between gap-2 font-serif text-xs text-cream-soft"
              >
                <span className="text-cream-faint w-20 shrink-0">{dayLabel(new Date(b.start))}</span>
                <span className="min-w-0 truncate text-right">
                  {b.id === booking.id ? (
                    <span className="text-gold-bright">this booking</span>
                  ) : (
                    (b.designBrief ?? 'sitting')
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() =>
            picked && setPlaced({ iso: studioDayStartIso(picked.key), label: dayLabel(picked.date) })
          }
          disabled={!picked}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="owm-place-date"
        >
          Place date <ChevronRight size={13} />
        </button>
      </div>

      {canCancel ? (
        <>
          <label className="block border-t border-gold-dim/20 pt-3">
            <span className="label-small-caps">Cancel reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. client will not pay - cancel and free the slot."
              className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
              data-testid="owm-cancel-reason"
            />
          </label>
          <CancelConsequence booking={booking} clockIso={clockIso} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={!reason.trim()}
              className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="owm-cancel"
            >
              <Ban size={13} /> Cancel booking
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

const DAY_CLASSES: Record<DayState, string> = {
  appt: 'border-gold-bright/60 bg-gold-bright/10 text-gold-bright font-semibold',
  current: 'border-blood/50 bg-blood/10 text-blood font-semibold',
  booked: 'border-azure/40 bg-azure/10 text-azure',
  selected: 'border-mint/60 bg-mint/20 text-mint font-semibold',
  muted: 'border-transparent text-cream-faint/40',
  unselectable: 'border-transparent text-cream-faint/50',
};

// State 1's month grid: prev/next nav + Mon-first day cells + legend. Pure
// presentation - all date logic arrives as `dayState`/`isSelectable` closures.
function ExtendCalendar({
  viewMonth,
  days,
  dayState,
  isSelectable,
  legendArtistName,
  onPick,
  onNavMonth,
}: {
  viewMonth: Date;
  days: Date[];
  dayState: (day: Date) => DayState | undefined;
  isSelectable: (day: Date) => boolean;
  legendArtistName: string;
  onPick: (day: Date) => void;
  onNavMonth: (delta: number) => void;
}) {
  const ariaFor = (day: Date, state: DayState | undefined): string => {
    const base = format(day, 'EEEE d MMMM yyyy');
    const suffix =
      state === 'appt'
        ? ' · this appointment'
        : state === 'current'
          ? ' · current top-up date'
          : state === 'booked'
            ? ` · ${legendArtistName} booked`
            : state === 'selected'
              ? ' · selected new date'
              : '';
    return base + suffix;
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onNavMonth(-1)}
          className="btn-quiet text-xs"
          aria-label="Previous month"
          data-testid="owm-cal-prev"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="font-serif text-sm text-cream">{format(viewMonth, 'MMMM yyyy')}</span>
        <button
          type="button"
          onClick={() => onNavMonth(1)}
          className="btn-quiet text-xs"
          aria-label="Next month"
          data-testid="owm-cal-next"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-7 gap-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="text-center label-small-caps text-cream-faint py-1">
            {d}
          </div>
        ))}
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const state = dayState(day);
          const selectable = isSelectable(day);
          return (
            <button
              key={key}
              type="button"
              data-testid={`owm-cal-day-${key}`}
              {...(state ? { 'data-state': state } : {})}
              disabled={!selectable}
              aria-label={ariaFor(day, state)}
              onClick={() => selectable && onPick(day)}
              className={clsx(
                'aspect-square rounded-md border text-xs font-inter transition',
                state ? DAY_CLASSES[state] : 'border-gold-dim/25 text-cream-soft',
                selectable ? 'hover:border-gold-bright/50 cursor-pointer' : 'cursor-not-allowed',
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-cream-faint">
        <LegendDot cls="bg-blood" label="Current top-up date" />
        <LegendDot cls="bg-mint" label="New date" />
        <LegendDot cls="bg-azure" label={`${legendArtistName} booked`} />
        <LegendDot cls="bg-gold-bright" label="This appointment" />
      </div>
    </div>
  );
}

// State 2: the placed date confirmation + SMS preview + confirm/back actions.
function ExtendConfirmState({
  oldLabel,
  newLabel,
  smsBody,
  phone,
  onChangeDate,
  onConfirm,
}: {
  oldLabel: string;
  newLabel: string;
  smsBody: string;
  phone?: string;
  onChangeDate: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <div
        className="flex items-center justify-between gap-3 rounded-md border border-mint/50 bg-mint/10 px-3 py-3"
        data-testid="owm-placed"
      >
        <div className="min-w-0">
          <p className="label-small-caps text-mint">New top-up date placed</p>
          <p className="font-serif text-sm text-cream mt-1">
            <span className="text-cream-faint line-through mr-2">{oldLabel}</span>
            <span className="text-cream font-semibold">{newLabel}</span>
          </p>
        </div>
        <span className="grid h-7 w-7 place-items-center rounded-full bg-mint/20 text-mint shrink-0">
          <Check size={15} />
        </span>
      </div>
      <button
        type="button"
        onClick={onChangeDate}
        className="self-start text-xs text-gold-bright hover:text-gold-bright/80 transition"
        data-testid="owm-change-date"
      >
        ← Change date
      </button>

      <div>
        <span className="label-small-caps">Text the customer will receive</span>
        <div
          className="mt-2 rounded-md border border-gold-dim/30 bg-ink-black/30 overflow-hidden"
          data-testid="owm-sms-preview"
        >
          {phone ? (
            <p className="px-3 py-1.5 border-b border-gold-dim/20 font-mono text-[11px] text-cream-faint">
              SMS · to {phone}
            </p>
          ) : null}
          <p className="px-3 py-2.5 font-serif text-sm text-cream">{smsBody}</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-auto">
        <button
          type="button"
          onClick={onChangeDate}
          className="inline-flex items-center gap-1.5 rounded-md border border-gold-dim/40 px-3 py-1.5 text-xs text-cream-soft hover:border-gold-bright/50 transition"
          data-testid="owm-back-to-calendar"
        >
          <ChevronLeft size={13} /> Back to calendar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition"
          data-testid="owm-extend"
        >
          <Check size={13} /> Confirm &amp; text customer
        </button>
      </div>
    </>
  );
}

function LegendDot({ cls, label }: { cls: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <i className={clsx('h-2 w-2 rounded-full', cls)} /> {label}
    </span>
  );
}

function UnpaidArtistBody({
  studioId,
  artistName,
  amountAud,
  dayIso,
  draft,
  setDraft,
  sent,
  onSend,
  onOpenThread,
}: {
  studioId: StudioId;
  artistName: string;
  amountAud: number;
  dayIso: string;
  draft: string;
  setDraft: (v: string) => void;
  sent: boolean;
  onSend: (managerId: string, body: string) => void;
  onOpenThread: () => void;
}) {
  const agents = useBrainStore((s) => s.agents);
  const manager = useMemo(
    () =>
      Object.values(agents).find(
        (a) =>
          a.role === 'studio-manager' &&
          (a.primaryStudio === studioId || a.assignedStudios.includes(studioId)),
      ) ?? Object.values(agents).find((a) => a.role === 'studio-manager'),
    [agents, studioId],
  );
  const managerName = manager
    ? `${manager.firstName} ${manager.lastName}`.trim()
    : 'the studio manager';

  const template = useMemo(
    () =>
      `Hi ${manager?.firstName ?? 'there'} - ${artistName} is owed ${aud(amountAud)} for ${studioDisplay(
        studioId,
      )} on ${simDateShort(dayIso)}. Can you sort the payout and confirm back here? Thanks.`,
    [manager, artistName, amountAud, studioId, dayIso],
  );
  const body = draft || template;

  return (
    <>
      <p className="font-serif text-xs text-cream-soft">
        Ops follows up the studio manager, not the artist. This opens a logged, WhatsApp-style
        thread to {managerName} with the amount, artist and date pre-filled.
      </p>
      <label className="block">
        <span className="label-small-caps">Message to {managerName}</span>
        <textarea
          value={body}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-gold-bright resize-none"
          data-testid="owm-message-draft"
        />
      </label>
      <div className="flex items-center justify-end gap-2">
        {sent ? (
          <>
            <span className="font-mono text-[11px] text-mint" data-testid="owm-message-sent">
              Sent - awaiting reply in the thread.
            </span>
            <button
              type="button"
              onClick={onOpenThread}
              data-testid="owm-open-thread"
              className="inline-flex items-center gap-1 rounded-md border border-gold-dim/40 px-2 py-1 text-[11px] text-cream-soft hover:border-gold-bright/50 transition"
            >
              Open thread
            </button>
          </>
        ) : null}
        <button
          type="button"
          onClick={() => manager && onSend(manager.id, body)}
          disabled={!manager || sent}
          className="btn-soft"
          data-testid="owm-message-manager"
        >
          <MessageSquare size={13} /> {manager ? `Message floor manager ${manager.firstName}` : 'Message floor manager'}
        </button>
      </div>
    </>
  );
}

// --- the Ops cancel 7-day consequence (W5 m9) --------------------------------

/**
 * What an Ops cancel will do to the money, shown BEFORE Ops confirms. A dry run
 * of the same pure split the cancel applies (applyCancelSplit, shared with the
 * server intent), so the preview can't disagree with the outcome.
 */
function CancelConsequence({ booking, clockIso }: { booking: Booking; clockIso: string }) {
  const deposits = useBrainStore((s) => s.deposits);
  const deposit = booking.depositId ? deposits[booking.depositId] : undefined;
  const split = applyCancelSplit<Booking, Deposit>({
    booking,
    deposit,
    reason: 'preview',
    by: 'preview',
    atIso: clockIso,
  });
  if (!split.ok) return null;
  const paid = deposit ? aud(ledgerPaidAud(deposit)) : '';
  const copy = {
    forfeited: {
      title: 'Deposit forfeited',
      body: `Inside 7 days: the ${paid} paid forfeits to the house. The slot is freed to the Sweeper.`,
      tone: 'border-blood/40 bg-blood/5 text-blood',
    },
    'kept-on-file': {
      title: 'Kept on file → Sweeper',
      body: `Outside 7 days: the ${paid} stays on file for 12 months and the client goes to the Sweeper to rebook.`,
      tone: 'border-azure/40 bg-azure/5 text-azure',
    },
    'link-voided': {
      title: 'Unpaid link voided',
      body: "No money taken: the open deposit link is voided so it can't be paid for a dead slot. The slot is freed to the Sweeper.",
      tone: 'border-gold-dim/40 bg-ink-black/30 text-gold-bright',
    },
    none: {
      title: 'No deposit on file',
      body: 'Nothing to forfeit. The slot is freed to the Sweeper.',
      tone: 'border-gold-dim/40 bg-ink-black/30 text-gold-bright',
    },
  }[split.depositOutcome];
  return (
    <div className={clsx('rounded-md border px-3 py-2.5', copy.tone)} data-testid="owm-cancel-consequence">
      <p className="label-small-caps">If you cancel · {copy.title}</p>
      <p className="font-serif text-xs text-cream-soft mt-1">{copy.body}</p>
    </div>
  );
}

/**
 * D01-17: Ops has dealt with the client's move request (moved it on the
 * calendar, talked it through, or said no): it comes off the plan and the list.
 */
function MoveRequestDone({ planId }: { planId: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setFailed(false);
          const ok = await clearTopupMoveRequest(planId);
          setBusy(false);
          if (!ok) setFailed(true);
        }}
        className="btn-ghost text-xs disabled:opacity-40"
        data-testid="owm-move-request-done"
      >
        {busy ? 'Saving…' : 'Request dealt with'}
      </button>
      {failed ? (
        <span role="alert" className="text-xs text-blood">
          It wasn&apos;t saved. Try again.
        </span>
      ) : null}
    </>
  );
}
